import React from 'react';
import { buildLetterModel } from './utils/letterPdf';

const NAVY = '#1A1F71';
const PINK = '#E6007E';

// Renders text with the first two letters of each word in pink, the rest in navy —
// mirrors the accent title used in the PDF header.
const AccentText = ({ text, fontSize }) => (
  <span style={{ fontWeight: 'bold', fontSize }}>
    {text.split(' ').map((word, i) => (
      <React.Fragment key={i}>
        {i > 0 ? ' ' : ''}
        <span style={{ color: PINK }}>{word.slice(0, 2)}</span>
        <span style={{ color: NAVY }}>{word.slice(2)}</span>
      </React.Fragment>
    ))}
  </span>
);

const Heading = ({ children }) => (
  <h3 style={{ color: NAVY, fontSize: '14px', margin: '16px 0 6px' }}>{children}</h3>
);

const Bullets = ({ items }) => (
  <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '0 0 8px' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: '8px', margin: '2px 0' }}>
        <span style={{ color: PINK }}>•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/**
 * On-screen preview of the welcome letter. Shares buildLetterModel with the PDF
 * generator so the preview and the downloaded PDF always match.
 */
function LetterPreview({ student, stays, bookings, letterDate, addressee }) {
  const model = buildLetterModel({ student, stays, bookings, letterDate, addressee });

  const logo = process.env.PUBLIC_URL + '/assets/img/blissLogo.png';
  const footer = process.env.PUBLIC_URL + '/assets/img/footer.png';

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #ddd',
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        padding: '32px',
        margin: '0 auto',
        maxWidth: '17cm',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: '13px',
        lineHeight: 1.5,
        color: '#282828',
        textAlign: 'left',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <img alt="BLISS" src={logo} style={{ height: '60px' }} />
        <AccentText text={model.title} fontSize="22px" />
      </div>
      <hr style={{ border: 'none', borderTop: `1px solid ${NAVY}`, margin: '10px 0 16px' }} />

      {/* Date + salutation */}
      <div style={{ textAlign: 'right' }}>{model.formattedDate}</div>
      <p style={{ fontWeight: 'bold', margin: '12px 0' }}>Dear {model.recipient},</p>

      {/* Welcome */}
      <p style={{ margin: '0 0 12px' }}>{model.welcome}</p>

      {/* Activities & programme */}
      <Heading>Activities &amp; programme</Heading>
      {model.programmeBullets.length > 0 ? (
        <>
          <p style={{ margin: '0 0 6px' }}>{model.programmeIntro}</p>
          <Bullets items={model.programmeBullets} />
        </>
      ) : (
        <p style={{ margin: 0 }}>{model.programmeFallback}</p>
      )}

      {/* What to bring */}
      <Heading>What to bring</Heading>
      <p style={{ margin: '0 0 6px' }}>{model.kitIntro}</p>
      <Bullets items={model.kitList} />

      {/* Permissions */}
      <Heading>Permissions on file</Heading>
      {model.permissionsGranted && <p style={{ margin: '0 0 6px' }}>{model.permissionsGranted}</p>}
      {model.permissionsMissing && <p style={{ margin: 0 }}>{model.permissionsMissing}</p>}

      {/* Dietary & medical */}
      {model.dietaryNote && (
        <>
          <Heading>Dietary &amp; medical notes</Heading>
          <p style={{ margin: 0 }}>{model.dietaryNote}</p>
        </>
      )}

      {/* Sign-off */}
      <p style={{ margin: '16px 0 0' }}>{model.closingParagraph}</p>
      <p style={{ margin: '12px 0 2px' }}>{model.signOffLine}</p>
      <p style={{ fontWeight: 'bold', margin: 0 }}>{model.teamLine}</p>

      {/* Footer */}
      <hr style={{ border: 'none', borderTop: `1px solid ${NAVY}`, margin: '20px 0 8px' }} />
      <div style={{ textAlign: 'center' }}>
        <img alt="info@brownleeschools.com" src={footer} style={{ height: '40px' }} />
      </div>
    </div>
  );
}

export default LetterPreview;
